import React from 'react';
import socketService from 'services/socketService';
import { currentUserId } from './useCurrentUserId';

/**
 * "Another agent is typing" — the one presence signal this integration has.
 *
 * ★ THE CUSTOMER NEVER APPEARS HERE. Meta's Cloud API has no presence or
 * typing events for the person on the phone; the server's `wa:v2:typing`
 * rebroadcasts an AGENT's keystrokes to the other agents in the same thread's
 * room. The label says so. A "typing…" under the customer's name would be a
 * lie the interface cannot back up.
 *
 * ★ IDENTIFIERS ONLY. The event carries `{ conversationId, userId }` and that
 * is all that is stored; the name is resolved locally (useAgentName). Nothing
 * from the socket payload is ever rendered as text.
 *
 * A module store rather than Redux: the entries expire on their own and no
 * other screen cares.
 */
const TTL_MS = 3000;
const EMIT_EVERY_MS = 2500;

const store = new Map(); // conversationId -> { userId, until }
const listeners = new Set();
const lastEmit = new Map();

const notify = () => listeners.forEach((l) => l());

/** Called by the socket hook. */
export function noteTyping({ conversationId, userId }) {
	if (!conversationId || !userId) return;
	// Our own keystrokes come back only on a second tab; never show them.
	if (String(userId) === currentUserId()) return;
	store.set(String(conversationId), { userId: String(userId), until: Date.now() + TTL_MS });
	notify();
}

/** Called by the composer. Throttled so a fast typist is one emit per 2.5s. */
export function emitTyping(conversationId) {
	if (!conversationId) return;
	const now = Date.now();
	if ((lastEmit.get(conversationId) ?? 0) + EMIT_EVERY_MS > now) return;
	lastEmit.set(conversationId, now);
	// ⚠ The no-op ack matters: without a callback `socketService.emit` wraps the
	// emit in a Promise that waits for an ack the server never sends.
	socketService.emit('wa:v2:typing', { conversationId }, () => {});
}

/** The typing entry for a thread, or null. Expires by itself. */
export function useTyping(conversationId) {
	const [entry, setEntry] = React.useState(null);

	React.useEffect(() => {
		if (!conversationId) return undefined;
		const id = String(conversationId);
		let timer = null;
		const sync = () => {
			const e = store.get(id);
			const live = e && e.until > Date.now() ? e : null;
			// Same identity when nothing changed, so the row does not re-render
			// for nothing.
			setEntry((prev) => (Boolean(prev) === Boolean(live) && prev?.userId === live?.userId ? prev : live));
			// Only a live entry needs a wake-up — to expire itself. Fifty idle
			// list rows must not each run a timer.
			clearTimeout(timer);
			if (live) timer = setTimeout(sync, Math.max(50, live.until - Date.now() + 10));
		};
		sync();
		listeners.add(sync);
		return () => {
			listeners.delete(sync);
			clearTimeout(timer);
		};
	}, [conversationId]);

	return entry;
}
